import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useClientContext } from "../../../_context/ClientContext";
import { useCreateReportNotificationContext } from "../../../_context/CreateReportNotificationContext";
import { ClientType } from "../../../_models/Client.model";
import { useReportContext } from "../../../_context/ReportContext";
import { useUserContext } from "../../../_context/UserContext";
import { ManualDateInput } from "../../../htmlComponents/ManualDateInput";
import "../../Admin/AddUsers/AddUsers.css";
import "../Reports.css";

/* ── SVG Icons ── */
const InternalIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h18" /><path d="M12 3v18" />
  </svg>
);

const InspectionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" /><path d="M12 18v-6" /><path d="M9 15h6" />
  </svg>
);

export const ConfirmClient = () => {
  const area_based_scoring_types = process.env.REACT_APP_AREA_BASED_SCORING_TYPES?.split(",");
  const navigate = useNavigate();
  const { clientForReporting } = useClientContext();
  const { setNewReport, updateReportedDate, setManualReportedDate, newReport } = useReportContext();
  const { setDisplayNotification, setNotification } = useCreateReportNotificationContext();
  const { userName } = useUserContext();

  useEffect(() => {
    if (!clientForReporting.Name) {
      setDisplayNotification(true);
      setNotification("Please select a Client to create reports");
      navigate("/reports/select_client_for_new_report");
      return;
    }
    if (!newReport.ReportedDate || newReport.ReportedDate === "2023-04-04") {
      updateReportedDate();
    }
  }, [clientForReporting.Name, navigate, setDisplayNotification, setNotification, newReport.ReportedDate, updateReportedDate]);

  const setIsInternalReport = (isInternal: boolean) => {
    setNewReport((reportOnCreate) => ({ ...reportOnCreate, IsInternalReport: isInternal }));

    if (isInternal) {
      navigate("/reports/create_new_report/scoring");
    } else {
      const needsConsent = newReport.Client.Type !== ClientType.Domestic && newReport.Client.Type !== ClientType.NDIS;
      navigate(needsConsent ? "/reports/create_new_report/client-consent" : "/reports/create_new_report/scoring");
    }
  };

  const isNdisOrDomestic = area_based_scoring_types?.includes(newReport.Client.Type.toString()) || newReport.Client.Type === ClientType.NDIS;

  return (
    <div className="reports-page-wrapper" style={{ maxWidth: 800 }}>
      {/* Premium Client Header Card */}
      <div className="rp-client-header">
        <h2 className="rp-client-title">Step 1: Report Type</h2>
        <div className="rp-client-email">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
          </svg>
          Selected Client: <strong>{clientForReporting.Name}</strong>
          <span className="au-role-pill admin" style={{ marginLeft: 8, background: "rgba(255,255,255,0.2)", color: "#fff" }}>
            {ClientType[clientForReporting.Type]}
          </span>
        </div>
      </div>

      {userName.toLowerCase().includes("ashan") && (
        <div className="au-card" style={{ marginBottom: 24, border: "1px solid #E5E7EB" }}>
          <div className="au-card-body" style={{ padding: "20px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <h5 style={{ fontWeight: 600, color: '#dc2626', margin: 0, fontSize: "0.95rem" }}>
                Administrator Override: Set Report Date
              </h5>
              <span className="au-role-pill admin" style={{ fontSize: "0.75rem", background: "rgba(220,38,38,0.1)", color: "#dc2626" }}>
                Ashan Only
              </span>
            </div>
            <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Set a custom date for this report using the calendar date picker or the dropdowns.
            </p>
            <div style={{ maxWidth: 640 }}>
              <ManualDateInput
                onDateChange={setManualReportedDate}
                initialDate={newReport.ReportedDate}
              />
            </div>
          </div>
        </div>
      )}

      <div className="au-card">
        <div className="au-card-header">
          <div className="au-card-header-icon" style={{ background: "rgba(109,67,185,0.1)", color: "#6d43b9" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" />
            </svg>
          </div>
          <div>
            <p className="au-card-header-title">Select Report Type</p>
            <p className="au-card-header-sub">Choose the type of quality assessment to create</p>
          </div>
        </div>

        <div className="au-card-body">
          <p style={{ fontSize: "0.9rem", color: "#4B5563", lineHeight: 1.6, marginBottom: 24 }}>
            In the CNA system, <strong>Internal Reports</strong> are for routine domestic cleaning updates by staff (no client consent required). <strong>Inspection Reports</strong> are formal termly supervisor inspections.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>

            {/* Internal Report Button */}
            <button
              className="admin-action-card"
              style={{ textAlign: "left", width: "100%", border: "1px solid #E5E7EB", appearance: "none", background: "#fff" }}
              onClick={() => setIsInternalReport(true)}
            >
              <div>
                <div className="admin-card-icon-wrap warning" style={{ background: "rgba(196,125,26,0.1)", color: "#C47D1A", marginBottom: 12 }}>
                  <InternalIcon />
                </div>
                <p className="admin-card-label" style={{ fontSize: "1rem" }}>Internal Report</p>
                <p className="admin-card-desc">Routine cleaner updates. No consent required.</p>
              </div>
            </button>

            {/* Inspection Report Button */}
            <button
              className="admin-action-card"
              style={{ textAlign: "left", width: "100%", border: "1px solid #E5E7EB", appearance: "none", background: "#fff" }}
              onClick={() => setIsInternalReport(false)}
            >
              <div>
                <div className="admin-card-icon-wrap success" style={{ background: "rgba(22,163,74,0.1)", color: "#16a34a", marginBottom: 12 }}>
                  <InspectionIcon />
                </div>
                <p className="admin-card-label" style={{ fontSize: "1rem" }}>
                  {isNdisOrDomestic ? (newReport.Client.Type === ClientType.NDIS ? 'NDIS Inspection' : 'Domestic Inspection') : 'Inspection Report'}
                </p>
                <p className="admin-card-desc">Formal termly inspections. Consent may be required.</p>
              </div>
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};
